import {
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
  toast,
} from '@nudool/design-system';
import { MAX_SEARCH_TERM_LENGTH, todayBusinessDate } from '@nudool/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { UsersIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { buildListEmptyMessage } from '../../lib/build-list-empty-message.js';
import { countCustomerListFilters } from '../../modules/customers/count-customer-list-filters.js';
import { makeCustomerColumns } from '../../modules/customers/customer-columns.js';
import { customerComputedSortFieldsFor } from '../../modules/customers/customer-computed-sort-fields-for.js';
import { CUSTOMER_DEFAULT_SORT } from '../../modules/customers/customer-default-sort.js';
import { customerListColumnVariables } from '../../modules/customers/customer-list-column-variables.js';
import type { CustomerListRow } from '../../modules/customers/customer-list-row.js';
import { CustomerListToolbarFilters } from '../../modules/customers/customer-list-toolbar-filters.js';
import { buildCustomerListWhere } from '../../modules/customers/customer-list-where.js';
import { CUSTOMER_SORT_MAP } from '../../modules/customers/customer-sort-map.js';
import { isCustomerSelectionUnfiltered } from '../../modules/customers/is-customer-selection-unfiltered.js';
import {
  GET_CUSTOMERS,
  type CustomerWhere,
  type GetCustomersQueryResult,
  type GetCustomersQueryVariables,
} from '../../modules/customers/operations.js';
import { useComputedSortFallback } from '../../modules/customers/use-computed-sort-fallback.js';
import { useCustomerFormOptions } from '../../modules/customers/use-customer-form-options.js';
import { useCustomerListFilters } from '../../modules/customers/use-customer-list-filters.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { NewCustomerAction } from './-NewCustomerAction.js';

const CUSTOMER_LIST_STALE_MESSAGE =
  'Customer list may be out of date — refresh the page to see the latest.';

export const Route = createFileRoute('/customers/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: CustomersPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function CustomersPage() {
  const navigate = useNavigate();
  const { permissions, memberBrands = [], readableBrands = memberBrands } = usePermissions();
  const canEdit = permissions.includes('customer:edit');
  // A ref, since useListTable is declared below and owns the pagination reset the filters call.
  const resetPaginationRef = useRef<() => void>(() => {});
  const resetPagination = useCallback(() => resetPaginationRef.current(), []);
  const listFilters = useCustomerListFilters(
    resetPagination,
    memberBrands.length === 1 ? memberBrands[0]?.id : undefined,
  );
  const { activeStatuses, statusIds, paymentTermsIds, brandIds } = listFilters;
  const { customerStatuses, paymentTerms } = useCustomerFormOptions(true);
  const filterSelection = useMemo(
    () => ({ activeStatuses, statusIds, paymentTermsIds }),
    [activeStatuses, statusIds, paymentTermsIds],
  );
  const where = useMemo(() => buildCustomerListWhere(filterSelection), [filterSelection]);
  const listVariables = useMemo(
    () => ({
      brandIds: brandIds.length > 0 ? brandIds : undefined,
      ...customerListColumnVariables(permissions),
    }),
    [brandIds, permissions],
  );
  const computedSortFields = useMemo(
    () => customerComputedSortFieldsFor(permissions),
    [permissions],
  );

  const {
    rows: customers,
    loading,
    error,
    refetch,
    tableProps,
    search,
  } = useListTable<
    GetCustomersQueryResult,
    GetCustomersQueryVariables,
    CustomerListRow,
    CustomerWhere
  >({
    query: GET_CUSTOMERS,
    extractConnection: (data) => data?.customers,
    defaultSort: CUSTOMER_DEFAULT_SORT,
    sortMap: CUSTOMER_SORT_MAP,
    computedSortFields,
    where,
    variables: listVariables,
    rowScope: brandIds.join(),
    // A column that fails to load comes back null with an error, and keeps the rows.
    errorPolicy: 'all',
    sortTiebreakerField: 'id', // CustomerOrderBy lists `id` as sortable (widened for this tiebreaker pre-migration).
  });

  useEffect(() => {
    resetPaginationRef.current = tableProps.onFirstPage;
  });

  const sortRefused = useComputedSortFallback(error, tableProps, computedSortFields);

  const columns = useMemo(
    () =>
      makeCustomerColumns({
        canEdit,
        permissions,
        today: todayBusinessDate(),
        onChanged: () => {
          // onFirstPage() hasn't flushed yet, so pass `after` or refetch reuses the stale cursor.
          tableProps.onFirstPage();
          // Under errorPolicy 'all' a failed refetch resolves without data rather than rejecting, so both paths are checked.
          void refetch({ after: undefined })
            .then((result) => {
              if (!result.data) toast.error(CUSTOMER_LIST_STALE_MESSAGE);
            })
            .catch(() => {
              toast.error(CUSTOMER_LIST_STALE_MESSAGE);
            });
        },
      }),
    [canEdit, permissions, refetch, tableProps],
  );

  // Only a view no filter could widen may show the full-page empty state, since it also hides the filters.
  const isUnfilteredView =
    !search && brandIds.length === 0 && isCustomerSelectionUnfiltered(filterSelection);
  const hasActiveFilters = brandIds.length > 0 || countCustomerListFilters(filterSelection) > 0;

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Customers</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Manage buyer records — identity, commercial terms, and linked brands.
        </DimeCardDescription>
        <DimeCardAction>
          <NewCustomerAction />
        </DimeCardAction>
      </DimeCardHeader>
      {error && !sortRefused && customers.length === 0 ? (
        <ListErrorState
          title="Could not load customers"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : !loading && customers.length === 0 && isUnfilteredView ? (
        <ListEmptyState
          icon={<UsersIcon />}
          title="No customers yet"
          description="Create your first customer to start generating sales orders."
          action={<NewCustomerAction />}
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={customers}
          maxBodyHeight="viewport"
          searchColumn="name"
          searchPlaceholder="Search by name…"
          searchMaxLength={MAX_SEARCH_TERM_LENGTH}
          filters={
            <CustomerListToolbarFilters
              filters={listFilters}
              brands={readableBrands}
              customerStatuses={customerStatuses}
              paymentTerms={paymentTerms}
            />
          }
          showViewOptions
          viewOptionsMenuClassName="w-56"
          emptyMessage={buildListEmptyMessage('customers', search, hasActiveFilters)}
          onRowClick={(customer) => {
            void navigate({
              to: '/customers/$customerId',
              params: { customerId: customer.id },
            });
          }}
          loadingRowCount={tableProps.pageSize}
          exportFileName="customers"
          {...tableProps}
        />
      )}
    </DimeCard>
  );
}
